import {
  INTERACTIVE_LAYER_METADATA,
  isTopmostInteractiveLayer,
} from "@/features/map/utils/maplibreClickPriority";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import "../../styles/layer.css";
import { createRoot } from "react-dom/client";
import { LandPricePopupContent } from "@/features/map/components/LandPricePopupContent";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useMaplibreContext } from "@/features/map/components/mapLibre/context/MaplibreLayerContext";
import { isMaplibreMapAlive } from "@/features/map/utils/leafletMaplibreInternals";
import { LatLng, Popup } from "leaflet";
import { type MapLibreClickHandler } from "@/features/map/types/mapLibre";
import { registerPrintLayerAdapter } from "@/features/map/utils/printLayerAdapterRegistry";
import { createMaplibrePrintAdapter } from "@/features/map/utils/maplibrePrintAdapter";

interface Props {
  visible: boolean;
  initialZoomLevel: number;
}

export const LAND_PRICE_MIN_ZOOM = 15;
export const LAND_PRICE_MAX_ZOOM = 22;
const MIN_ZOOM = LAND_PRICE_MIN_ZOOM;
const MAX_ZOOM = LAND_PRICE_MAX_ZOOM;
const SOURCE_NAME = "landPrice";
const SOURCE_LAYER_NAME = "landprice";
// 他レイヤーの「国土数値情報」と完全一致させて attribution の重複を防ぐ
// ため、リンク付きの同一文字列を使う。
const ATTRIBUTION =
  '<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>';

// タイルデータが保存されているホスト名 例: https://example.com
const HOST = process.env.VITE_PUBLIC_LAND_PRICE_TILE_HOST ?? "";

// LaunchDarklyのフラグで切り替えるタイルプレフィックスのデフォルト値
const DEFAULT_TILE_YEAR_PREFIX = "2024";

/*
公示価格レイヤーのコンポーネント
MaplibreLayerContext が管理する単一の MapLibre インスタンスに対して
ソースとレイヤーを追加・削除する
*/
export const LandPriceTileLayer: React.FC<Props> = ({
  visible,
  initialZoomLevel,
}) => {
  // Leaflet マップオブジェクト
  const leafletMap = useMap();
  // MaplibreLayerContext から提供される Context
  const { maplibreMap } = useMaplibreContext();
  // ポップアップ格納用
  const popupRef = useRef<Popup | null>(null);

  // ズームレベル
  const [zoomLevel, setZoomLevel] = useState<number>(initialZoomLevel);

  // レイヤー ID
  const symbolLayerId = "landprice_type_symbol";
  const circleLayerId = "landprice_circle_layer";

  useEffect(() => {
    if (!maplibreMap) return;

    return registerPrintLayerAdapter(leafletMap, () =>
      createMaplibrePrintAdapter({
        map: maplibreMap,
        layers: [
          {
            id: symbolLayerId,
            visible,
            layoutPixelProperties: ["text-size", "text-padding"],
            paintPixelProperties: ["text-halo-width", "text-halo-blur"],
          },
          {
            id: circleLayerId,
            visible,
            paintPixelProperties: ["circle-radius", "circle-stroke-width"],
          },
        ],
      })
    );
  }, [leafletMap, maplibreMap, symbolLayerId, circleLayerId, visible]);

  // LaunchDarklyフラグ
  const { publiclandpriceDetailView, publicLandPriceTileYear } =
    useFeatureFlags();

  // 公示価格タイルのプレフィックス（年度）
  const tileYearPrefix =
    (publicLandPriceTileYear as string) ?? DEFAULT_TILE_YEAR_PREFIX;

  // クリックイベントハンドラ（leafletMap 依存）
  const clickHandler = useCallback<MapLibreClickHandler>(
    (e) => {
      if (
        !maplibreMap ||
        !isTopmostInteractiveLayer(maplibreMap, e.point, circleLayerId)
      )
        return;
      e.originalEvent.stopPropagation();

      const features = e.features;
      if (features && features.length > 0) {
        const feature = features[0];

        // feature.properties は MapLibre の型定義上 null/undefined にはならない想定だが
        // 万が一null/undefinedの場合はfeaturesが無い場合と同様になにもしない
        const properties = feature.properties;
        if (!properties) return;

        const id = feature.id as number;
        const coordinates = new LatLng(e.lngLat.lat, e.lngLat.lng);
        const landPrice = properties.land_price as number;
        const locationChiban = properties.location_chiban as string;
        const year = properties.year as string;
        const jukyoHyoji = properties.jukyo_hyoji as string;

        const popupHTML = document.createElement("div");
        createRoot(popupHTML).render(
          <LandPricePopupContent
            id={id}
            coordinates={coordinates}
            landPrice={landPrice}
            locationChiban={locationChiban}
            year={year}
            jukyoHyoji={jukyoHyoji}
            enableLandPriceDetail={publiclandpriceDetailView as boolean}
          />
        );

        if (popupRef.current) {
          popupRef.current.remove();
        }

        // leafletのポップアップを表示する
        popupRef.current = new Popup({
          content: popupHTML,
          closeButton: true,
          closeOnClick: false,
        })
          .setLatLng(coordinates)
          .openOn(leafletMap);

        // CSS-in-JS のスタイル注入完了後に幅を再計算
        // DOM 要素を渡した場合、Leaflet の初回幅測定時に Emotion のスタイル注入が
        // 完了していない可能性があるため、次フレームで update() を呼んで再測定する
        // こうすることでポップアップの幅が正しく表示される
        requestAnimationFrame(() => {
          popupRef.current?.update();
        });
      }
    },
    [leafletMap, maplibreMap, circleLayerId, publiclandpriceDetailView]
  );

  const setupLayers = useCallback((): void => {
    if (!maplibreMap) return;

    // Leaflet の attribution コントロールに出典を追加
    leafletMap.attributionControl.addAttribution(ATTRIBUTION);

    // ソースがまだ追加されていない場合は追加
    if (!maplibreMap.getSource(SOURCE_NAME)) {
      maplibreMap.addSource(SOURCE_NAME, {
        type: "vector",
        tiles: [`${HOST}/${tileYearPrefix}/{z}/{x}/{y}.pbf`],
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
      });
    }

    // シンボルレイヤー（ラベル）
    if (!maplibreMap.getLayer(symbolLayerId)) {
      maplibreMap.addLayer({
        id: symbolLayerId,
        type: "symbol",
        source: SOURCE_NAME,
        "source-layer": SOURCE_LAYER_NAME,
        layout: {
          "text-field": [
            "string",
            ["number-format", ["get", "land_price"], { currency: "JPY" }],
          ],
          "text-font": ["Open Sans Regular"],
          "text-size": 20,
          "text-padding": 2,
          "text-ignore-placement": false,
          "text-anchor": "bottom",
          "text-radial-offset": 1,
          visibility: "none",
        },
        paint: {
          "text-color": "#aa00ff",
          "text-halo-color": "#FFFFFF",
          "text-halo-width": 3,
          "text-halo-blur": 0,
        },
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
        filter: ["==", ["geometry-type"], "Point"],
      });
    }

    // サークルレイヤー
    if (!maplibreMap.getLayer(circleLayerId)) {
      maplibreMap.addLayer({
        id: circleLayerId,
        metadata: INTERACTIVE_LAYER_METADATA,
        type: "circle",
        source: SOURCE_NAME,
        "source-layer": SOURCE_LAYER_NAME,
        paint: {
          "circle-color": "#4264fb",
          "circle-radius": 10,
          "circle-stroke-width": 3,
          "circle-stroke-color": "#ffffff",
        },
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
        filter: ["==", ["geometry-type"], "Point"],
        layout: {
          visibility: "none",
        },
      });
    }
  }, [maplibreMap, tileYearPrefix, leafletMap]);

  // レイヤー初期化を行う
  useEffect(() => {
    if (!maplibreMap) return;

    const handleLoad = (): void => {
      setupLayers();
    };

    if (maplibreMap.isStyleLoaded()) {
      setupLayers();
    } else {
      maplibreMap.on("load", handleLoad);
    }

    return () => {
      maplibreMap.off("load", handleLoad);

      // 画面遷移で地図ごと破棄された場合、maplibre は内部 style を失っており
      // getLayer 等を呼ぶと例外になる。地図破棄時は個別削除も不要なのでスキップする。
      if (isMaplibreMapAlive(maplibreMap)) {
        // レイヤーを先に削除してからソースを削除（MapLibreの制約）
        if (maplibreMap.getLayer(symbolLayerId)) {
          maplibreMap.removeLayer(symbolLayerId);
        }

        if (maplibreMap.getLayer(circleLayerId)) {
          maplibreMap.removeLayer(circleLayerId);
        }

        if (maplibreMap.getSource(SOURCE_NAME)) {
          maplibreMap.removeSource(SOURCE_NAME);
        }
      }

      leafletMap.attributionControl.removeAttribution(ATTRIBUTION);
    };
  }, [maplibreMap, setupLayers, symbolLayerId, circleLayerId, leafletMap]);

  // ズームレベル変化を監視
  useEffect(() => {
    if (!leafletMap) return;

    const handleZoomEnd = (): void => {
      setZoomLevel(leafletMap.getZoom());
    };

    leafletMap.on("zoomend", handleZoomEnd);
    return () => {
      leafletMap.off("zoomend", handleZoomEnd);
    };
  }, [leafletMap]);

  // ズームレベルに応じた表示制御
  useEffect(() => {
    if (!maplibreMap) return;

    const shouldShow =
      visible &&
      zoomLevel &&
      zoomLevel - 1 >= MIN_ZOOM &&
      zoomLevel - 1 <= MAX_ZOOM;

    [symbolLayerId, circleLayerId].forEach((layerId) => {
      if (maplibreMap.getLayer(layerId)) {
        maplibreMap.setLayoutProperty(
          layerId,
          "visibility",
          shouldShow ? "visible" : "none"
        );
      }
    });

    if (shouldShow) {
      maplibreMap.on("click", circleLayerId, clickHandler);
    } else {
      maplibreMap.off("click", circleLayerId, clickHandler);
    }

    return () => {
      maplibreMap.off("click", circleLayerId, clickHandler);
    };
  }, [
    visible,
    zoomLevel,
    maplibreMap,
    symbolLayerId,
    circleLayerId,
    clickHandler,
  ]);

  // ポップアップのクリーンアップ
  useEffect(() => {
    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    };
  }, []);

  return null;
};
